/**
 * Enhanced Disclosure History Component
 *
 * Shows detailed disclosure tracking with:
 * - Per-disclosure events from database
 * - Per-recipient signature status
 * - Documents list
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle2, Clock, AlertCircle, FileText, User, Users, Loader2, EyeOff, Mail, Monitor, ChevronRight, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { disclosureTrackingService } from '@/services/disclosureTrackingService';
import type { DisclosureHistory, DisclosureEvent, DisclosureRecipient } from '@/types/disclosure-tracking';
import { cn } from '@/lib/utils';

interface EnhancedDisclosureHistoryProps {
  encompassLoanId: string;
}

// Format date with time
function formatDateTime(date: string | null): string {
  if (!date) {
return '-';
}
  try {
    return new Date(date).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return '-';
  }
}

// Get status badge for recipient
function RecipientStatusBadge({ status }: { status: DisclosureRecipient['status'] }) {
  switch (status) {
    case 'signed':
      return (
        <Badge className="bg-green-100 text-green-800 text-xs">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Signed
        </Badge>
      );
    case 'viewed':
      return (
        <Badge className="bg-blue-100 text-blue-800 text-xs">
          <FileText className="h-3 w-3 mr-1" />
          Viewed
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-amber-100 text-amber-800 text-xs">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </Badge>
      );
    case 'declined':
      return (
        <Badge className="bg-red-100 text-red-800 text-xs">
          <AlertCircle className="h-3 w-3 mr-1" />
          Declined
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-xs">
          {status}
        </Badge>
      );
  }
}

// Get recipient type icon
function RecipientIcon({ type }: { type: DisclosureRecipient['type'] }) {
  switch (type) {
    case 'Borrower':
    case 'CoBorrower':
      return <User className="h-3 w-3" />;
    case 'LoanAssociate':
      return <Users className="h-3 w-3" />;
    default:
      return <User className="h-3 w-3" />;
  }
}

// Helper to compute signature counts by recipient type
function getSignatureCounts(recipients: DisclosureRecipient[]) {
  const borrowers = recipients.filter(r => r.type === 'Borrower' || r.type === 'CoBorrower');
  const loanAssociates = recipients.filter(r => r.type === 'LoanAssociate');

  return {
    borrowers: {
      signed: borrowers.filter(r => r.status === 'signed').length,
      total: borrowers.length
    },
    loanAssociates: {
      signed: loanAssociates.filter(r => r.status === 'signed').length,
      total: loanAssociates.length
    }
  };
}

// Single disclosure event row
function DisclosureEventCard({ event }: { event: DisclosureEvent }) {
  const [expanded, setExpanded] = useState(false);

  // Excluded-from-timeline rows render with dashed/muted styling. The canonical
  // signal lives on the DTO as includedInTimeline; UI reads it directly.
  const isExcluded = !event.includedInTimeline;

  const counts = getSignatureCounts(event.recipients);
  const allBorrowersSigned = counts.borrowers.total > 0 && counts.borrowers.signed === counts.borrowers.total;
  const allLOSigned = counts.loanAssociates.total > 0 && counts.loanAssociates.signed === counts.loanAssociates.total;

  // Get method icon and label
  const getMethodInfo = () => {
    switch (event.disclosedMethod) {
      case 'ByMail':
        return { icon: <Mail className="h-3 w-3" />, label: 'Mail', className: 'bg-orange-100 text-orange-700' };
      case 'eDisclosure':
        return { icon: <Monitor className="h-3 w-3" />, label: 'eSign', className: 'bg-cyan-100 text-cyan-700' };
      case 'eClose':
        return { icon: <Monitor className="h-3 w-3" />, label: 'eClose', className: 'bg-emerald-100 text-emerald-700' };
      case 'InPerson':
        return { icon: <User className="h-3 w-3" />, label: 'In Person', className: 'bg-amber-100 text-amber-700' };
      default:
        return { icon: null, label: event.disclosedMethod || '-', className: 'bg-gray-100 text-gray-600' };
    }
  };

  const methodInfo = getMethodInfo();

  return (
    <div className={cn(
      "border rounded-lg mb-2 overflow-hidden",
      isExcluded ? "border-dashed border-muted-foreground/30" : "border-border"
    )}>
      {/* Compact Row - tight flexbox with gap-2 */}
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors",
          isExcluded ? "bg-muted/30" : "hover:bg-muted/40"
        )}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Expand Icon */}
        <div className="text-muted-foreground shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>

        {/* Date - fixed width for alignment */}
        <div className="text-xs text-muted-foreground w-[100px] shrink-0">
          {formatDateTime(event.disclosedDate)}
        </div>

        {/* Form Type Badge (LE/CD) - colored */}
        <Badge
          className={cn(
            'text-[10px] px-1.5 py-0 shrink-0',
            event.formType === 'LE' && 'bg-blue-100 text-blue-700',
            event.formType === 'CD' && 'bg-purple-100 text-purple-700',
            event.formType === 'TIL' && 'bg-gray-100 text-gray-700',
            event.formType === 'Other' && 'bg-gray-100 text-gray-600'
          )}
        >
          {event.formType}
        </Badge>

        {/* Disclosure Type Badge (Initial/Revised) - outline badge */}
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] px-1.5 py-0 shrink-0',
            event.type === 'Initial' && 'border-green-400 text-green-700',
            event.type === 'Revised' && 'border-amber-400 text-amber-700',
            event.type === 'Final' && 'border-blue-400 text-blue-700',
            event.type === 'Corrected' && 'border-red-400 text-red-700'
          )}
        >
          {event.type}
        </Badge>

        {/* Method Badge */}
        <Badge className={cn('text-[10px] px-1.5 py-0 shrink-0 gap-0.5', methodInfo.className)}>
          {methodInfo.icon}
          {methodInfo.label}
        </Badge>

        {/* Document count badge - clickable to show full list */}
        {(event.documentCount ?? event.documents.length) > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 shrink-0 text-muted-foreground gap-0.5 cursor-pointer hover:bg-muted"
                onClick={(e) => e.stopPropagation()}
              >
                <FileText className="h-2.5 w-2.5" />
                {event.documentCount ?? event.documents.length} docs
              </Badge>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <div className="p-3 border-b">
                <h4 className="font-medium text-sm">Documents Sent</h4>
                <p className="text-xs text-muted-foreground">
                  {event.documents.length} documents in this disclosure
                </p>
              </div>
              <ScrollArea className="h-[300px]">
                <div className="p-2 space-y-1">
                  {event.documents.map((doc, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 p-2 rounded hover:bg-muted/50 text-xs"
                    >
                      <FileText className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{doc.formName}</div>
                        <div className="text-muted-foreground">
                          {doc.signatureType || doc.formType}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}

        {/* ITOP Badge (if applicable) */}
        {event.hasItop && (
          <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 shrink-0">
            ITOP
          </Badge>
        )}

        {/* Excluded Badge */}
        {isExcluded && (
          <Badge className="bg-slate-100 text-slate-500 text-[10px] px-1.5 py-0 shrink-0 gap-0.5">
            <EyeOff className="h-2.5 w-2.5" />
            Excluded
          </Badge>
        )}

        {/* Separator - small visual divider instead of ml-auto gap */}
        <span className="text-muted-foreground/30 mx-1">|</span>

        {/* Sent By - NO ml-auto, keeps content together */}
        <div className="text-xs text-muted-foreground truncate max-w-[120px]">
          {event.disclosedBy || '-'}
        </div>

        {/* Signature Status - Separated by type with labels */}
        <div className="flex items-center gap-1.5 shrink-0">
          {counts.borrowers.total > 0 && (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] px-1.5 py-0",
                allBorrowersSigned
                  ? "border-green-400 text-green-700 bg-green-50"
                  : "border-amber-400 text-amber-700 bg-amber-50"
              )}
            >
              Borrower {counts.borrowers.signed}/{counts.borrowers.total}
            </Badge>
          )}
          {counts.borrowers.total > 0 && counts.loanAssociates.total > 0 && (
            <span className="text-muted-foreground/40">|</span>
          )}
          {counts.loanAssociates.total > 0 && (
            <Badge
              variant="outline"
              className={cn(
                "text-[10px] px-1.5 py-0",
                allLOSigned
                  ? "border-green-400 text-green-700 bg-green-50"
                  : "border-amber-400 text-amber-700 bg-amber-50"
              )}
            >
              LO {counts.loanAssociates.signed}/{counts.loanAssociates.total}
            </Badge>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t bg-muted/20 px-4 py-3">
          {/* Recipients */}
          {event.recipients.length > 0 && (
            <div className="mb-3">
              <h5 className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Recipients
              </h5>
              <div className="space-y-1.5">
                {event.recipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center gap-3 text-xs bg-background rounded px-2.5 py-1.5"
                  >
                    <div className="flex items-center gap-1.5 min-w-[140px]">
                      <RecipientIcon type={recipient.type} />
                      <span className="font-medium">{recipient.name}</span>
                    </div>
                    <span className="text-muted-foreground text-[11px] min-w-[80px]">
                      {recipient.role || recipient.type}
                    </span>
                    <RecipientStatusBadge status={recipient.status} />
                    <span className="text-muted-foreground text-[11px]">
                      {recipient.signedDate
                        ? formatDateTime(recipient.signedDate)
                        : recipient.viewedDate
                        ? `Viewed ${formatDateTime(recipient.viewedDate)}`
                        : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents (only show if we have any) */}
          {event.documents.length > 0 && (
            <div className="mb-3">
              <h5 className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Documents ({event.documents.length})
              </h5>
              <div className="flex flex-wrap gap-1.5">
                {event.documents.map((doc, idx) => (
                  <Badge key={idx} variant="outline" className="text-[10px] gap-1">
                    <FileText className="h-2.5 w-2.5" />
                    {doc.formName || 'Document'}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Additional Info */}
          {(event.safeHarborSent || event.providerListSent) && (
            <div className="flex gap-2">
              {event.safeHarborSent && (
                <Badge className="bg-teal-100 text-teal-700 text-[10px]">Safe Harbor</Badge>
              )}
              {event.providerListSent && (
                <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Provider List</Badge>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function EnhancedDisclosureHistory({ encompassLoanId }: EnhancedDisclosureHistoryProps) {
  const [history, setHistory] = useState<DisclosureHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadHistory() {
      setLoading(true);
      setError(null);

      try {
        const data = await disclosureTrackingService.getDisclosureHistory(encompassLoanId);
        setHistory(data);
      } catch {
        setError('Failed to load disclosure history');
      } finally {
        setLoading(false);
      }
    }

    loadHistory();
  }, [encompassLoanId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-xs text-muted-foreground">Loading disclosures...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-red-600 py-2">
        <AlertCircle className="h-3 w-3 inline mr-1" />
        {error}
      </div>
    );
  }

  if (!history || history.events.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic py-2">
        No disclosures sent yet
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium">
          Disclosure History
          <span className="text-muted-foreground font-normal ml-1.5">
            ({history.totalEvents} {history.totalEvents === 1 ? 'event' : 'events'})
          </span>
        </h4>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {history.signedCount} signed
          </span>
          {history.pendingCount > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="h-3.5 w-3.5" />
              {history.pendingCount} pending
            </span>
          )}
        </div>
      </div>

      {/* Events List */}
      <div>
        {history.events.map((event) => (
          <DisclosureEventCard key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
}
